import axios, { InternalAxiosRequestConfig, AxiosResponse } from "axios";
import {
  getAuthToken,
  removeAuthToken,
  removeUser,
  setAuthToken,
  setRefreshToken,
  setUser,
} from "../../utils/LocalStorageUtil";
import { camelizeKeys } from "humps";
import { BASE_URL } from "../api/AxiosHelper";
import { X_AUTH_REFRESH_TOKEN, X_AUTH_TOKEN } from "../../models/Constants";
import { toast } from "react-toastify";
const OPEN_URL_LIST: string[] = ["api/auth/login", "api/auth/register"];

const AUTHENTICATE: string = "api/auth/login";

export const destroyToken = () => {
  removeAuthToken();
  removeUser();
};

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

const handleServerError = (error: ApiError): Error | ApiError => {
  const message = error?.response?.data?.message;
  if (message) {
    toast.error(message); // ✅ Show toast
    return new Error(message);
  }
  toast.error("An unexpected error occurred."); // fallback message
  return error;
};

const createRequestInterceptor = (): void => {
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      const token: string | null = getAuthToken();

      //Ensure config.url is defined before replacing
      if (
        config.url &&
        !OPEN_URL_LIST.includes(config.url.replace(BASE_URL, "")) &&
        token
      ) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }

      config.headers["Content-Type"] = "application/json";

      //Decamelize playload (uncomment if needed)
      // if(config.method?.toUpperCase() === "POST" && config.data){
      //     config.data = decamelizeKeys(config.data, {split: /(?=[A-Z0-9])/})
      // }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

const createResponseInterceptor = (): void => {
  const interceptor = axios.interceptors.response.use(
    async (response: AxiosResponse): Promise<any> => {
      const { config, data, headers } = response;

      if (!config || !data) {
        return Promise.reject(new Error("Something went wrong"));
      }

      let responseData = data;

      if (
        config.url &&
        config.url.replace(BASE_URL, "").split("?")[0] !== "complaintFlow"
      ) {
        responseData = camelizeKeys(data);
      }
      // ✅ Show success toast if applicable
      // ✅ Show success toast only for POST/PUT/DELETE when status === 0
      const successMethods = ["POST", "PUT", "DELETE"];

      if (
        successMethods.includes(config.method?.toUpperCase() || "") &&
        data?.status === 0 &&
        data?.message
      ) {
        toast.success(data.message);
      }

      if (data?.status !== 0 && data?.message) {
        toast.error(data.message);
        return Promise.reject(new Error(data.message));
      }

      if (
        config.url?.replace(BASE_URL, "") === AUTHENTICATE &&
        data?.status === 0
      ) {
        setUser(responseData);
        if (headers) {
          setAuthToken(headers[X_AUTH_TOKEN.toLowerCase()]);
          setRefreshToken(headers[X_AUTH_REFRESH_TOKEN.toLowerCase()]);
        }
        window.location.href = "/";
      }
      return Promise.resolve(responseData);
    },
    async (error) => {
      console.error(error);

      const originalRequestUrl = error?.response?.config?.url?.replace(
        BASE_URL,
        ""
      );

      if (
        error?.response?.status !== 401 ||
        originalRequestUrl === AUTHENTICATE
      ) {
        return Promise.reject(handleServerError(error));
      }

      destroyToken();
      window.location.href = "/";

      axios.interceptors.response.eject(interceptor);

      try {
        const res = await axios.post(`${BASE_URL}api/auth/refresh-token`, null);
        setAuthToken(res.data.data);
        createResponseInterceptor();
        return await axios(error.response.config);
      } catch (voError: any) {
        destroyToken();
        createResponseInterceptor();
        return Promise.reject(handleServerError(voError));
      }
    }
  );
};

createRequestInterceptor();
createResponseInterceptor();
